import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ShopContext } from './context/ShopContext';
import { SpeedInsights } from "@vercel/speed-insights/react";
import Home from './pages/Home';
import Collection from './pages/Collection';
import About from './pages/About';
import Contact from './pages/Contact';
import Product from './pages/Product';
import Cart from './pages/Cart';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import PlaceOrder from './pages/PlaceOrder';
import Orders from './pages/Orders';
import OrderDetails from './pages/OrderDetails';
import Verify from './pages/Verify';
import Settings from './pages/UserSettings';
import OrderSuccess from './pages/OrderSuccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AuthSuccess from './pages/AuthSuccess';
import NotFound from './pages/NotFound';
import DevLogin from './pages/Dev/DevLogin';
import ErrorDashboard from './pages/Dev/ErrorDashboard';
import Uitest from './pages/Dev/Uitest';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SearchBar from './components/SearchBar';
import Loader from './components/Loader';
import NetworkStatus from './components/NetworkStatus';
import CountryBlockModal from './components/Modals/countryBlockModal';
import VPNModal from './components/Modals/vpnModal';
import CaptchaModal from './components/Modals/captchaModal';

import useCountryDetector from './utils/countryDetector';
import useVpnDetector from './utils/vpnDetector';
import Modal from './components/Modals/Modal';
import Location from './pages/Location';

const App = () => {
  const [state, setState] = useState({
    loadingProgress: 0,
    isDataLoaded: false,
    isOnline: navigator.onLine,
    isLoading: true
  });

  // VPN bypass localStorage logic
  const [isVpnBypassed, setIsVpnBypassed] = useState(false);
  useEffect(() => {
    // Check VPN bypass localStorage
    const vpnBypassData = localStorage.getItem('vpnBypass');
    if (vpnBypassData) {
      try {
        const { bypassed, expires } = JSON.parse(vpnBypassData);
        const now = Date.now();
        if (expires && now > expires) {
          localStorage.removeItem('vpnBypass');
          setIsVpnBypassed(false);
        } else if (bypassed) {
          setIsVpnBypassed(true);
        }
      } catch {
        localStorage.removeItem('vpnBypass');
      }
    }
  }, []);

  // On VPN bypass, set localStorage with 7 days expiry
  const handleVpnBypass = () => {
    const expires = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('vpnBypass', JSON.stringify({ bypassed: true, expires }));
    setIsVpnBypassed(true);
  };

  const { backendUrl } = useContext(ShopContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isDevRoute = location.pathname.startsWith('/_scc-internal-dev-system');

  const [bypassCountry, setBypassCountry] = useState(false);
  const { isRestricted, userCountry, detectionComplete } = useCountryDetector();

  const [bypassVpn, setBypassVpn] = useState(false);
  const { isVpn, vpnDetectionComplete } = useVpnDetector('m03148-7760x9-f8mm21-2uj039');

  const [captchaCompleted, setCaptchaCompleted] = useState(false);

  const logError = (error, context = {}) => {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message || String(error),
      stack: error.stack,
      context: { ...context, state, route: location.pathname }
    };

    try {
      const existingErrors = JSON.parse(localStorage.getItem('sccErrors') || '[]');
      localStorage.setItem('sccErrors', JSON.stringify([errorEntry, ...existingErrors].slice(0, 200)));
    } catch (e) {
    }
  };

  useEffect(() => {
    const handleError = (message, source, lineno, colno, error) =>
      logError(error || new Error(message), { type: 'window', source, lineno, colno, silent: true });

    const handleRejection = (event) =>
      logError(event.reason, { type: 'unhandledrejection', silent: true });

    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
      toast.dismiss();
      toast.success('Back online!', { autoClose: 2000 });
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false }));
      logError(new Error('Network offline'), { silent: true });
      toast.error('You are offline', { autoClose: false });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!state.isOnline || !detectionComplete || !vpnDetectionComplete) return;

    // VPN bypass logic
    const effectiveVpnBypass = bypassVpn || isVpnBypassed;

    if (isVpn && !effectiveVpnBypass && !captchaCompleted) {
      return;
    }

    if (isRestricted && !bypassCountry) {
      return;
    }

    const loadData = async () => {
      try {
        setState(prev => ({ ...prev, loadingProgress: 15 }));
        const response = await fetch(`${backendUrl}/api/product/list`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await response.json();
        setState(prev => ({ ...prev, loadingProgress: 100, isDataLoaded: true }));
      } catch (error) {
        logError(error, { endpoint: `${backendUrl}/api/product/list` });
        setState(prev => ({ ...prev, loadingProgress: 0 }));
      } finally {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    if ((!isVpn || effectiveVpnBypass || captchaCompleted) && (!isRestricted || bypassCountry)) {
      loadData();
    }

  }, [backendUrl, state.isOnline, detectionComplete, vpnDetectionComplete, isVpn, bypassVpn, isVpnBypassed, captchaCompleted, isRestricted, bypassCountry]);

  if (!state.isOnline && !state.isLoading) return <NetworkStatus />;

  if (isRestricted && !bypassCountry && detectionComplete) {
    return (
      <>
      {isRestricted && <Modal
          isOpen={true}
          title="Request Timeout"
          type="warning"
          children="Looks like you made many requests,We are unable to process your request at this time. Please try again later."
          onClose={window.location.reload}
      />}
      {isRestricted && userCountry && (
        <CountryBlockModal
          country={userCountry}
          onBypass={() => setBypassCountry(true)}
          onBack={() => navigate(-1)}
        />
      )}
    </>
  );
}

  if (isVpn && !bypassVpn && !captchaCompleted && vpnDetectionComplete) {
    return (
      <CaptchaModal
        onCaptchaSuccess={() => {
          setCaptchaCompleted(true);
        }}
      />
    );
  }

  if (isVpn && !bypassVpn && captchaCompleted && vpnDetectionComplete) {
    return (
      <VPNModal
        onBypass={() => {
          setBypassVpn(true);
          handleVpnBypass();
        }}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (!state.isDataLoaded && !isDevRoute) {
    return <Loader progress={state.loadingProgress} />;
  }

  return (
    <div className={`min-h-screen flex flex-col ${isDevRoute ? 'bg-gray-50' : 'bg-white'}`}>
      <ToastContainer />
      <div className={`flex-1 ${!isDevRoute ? 'px-4 sm:px-[5vw] md:px-[7vw] lg:px-[9vw]' : ''}`}>
        {!isDevRoute && (
          <>
            <Navbar />
            <SearchBar />
          </>
        )}
        <SpeedInsights/>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/catalog" element={<Collection />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/product/:productId" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/place-order" element={<PlaceOrder />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/order/:productId/date/:date" element={<OrderDetails />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/user/account/settings" element={<Settings />} />
          <Route path="/auth-success" element={<AuthSuccess />} />
          <Route path="/user/orders/success" element={<OrderSuccess />} />
          <Route path="/privacy-policies" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/_scc-internal-dev-system/login" element={<DevLogin />} />
          <Route path="/_scc-internal-dev-system/uitest" element={<Uitest />} />
          <Route path="/location" element={<Location/>}/>
          <Route
            path="/_scc-internal-dev-system/error-dashboard"
            element={localStorage.getItem('devAuthenticated') === 'true'
              ? <ErrorDashboard />
              : <Navigate to="/_scc-internal-dev-system/login" replace />}
          />
          <Route
            path="/_scc-internal-dev-system/dashboard"
            element={localStorage.getItem('devAuthenticated') === 'true'
              ? <SuperDashboard />
              : <Navigate to="/_scc-internal-dev-system/login" replace />}
          />
        </Routes>
        {!isDevRoute && <Footer />}
      </div>
    </div>
  );
};

export default App;